import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faClock,
  faFlag,
  faChevronLeft,
  faChevronRight,
  faEyeSlash,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../../Components/Common/Modal";
import { useEffect, useState } from "react";
import Button from "../../../../Components/Common/Button";

const QuizModal = ({ isOpen, onClose, questions, subchapterTitle }) => {
  // Find the index of the question with showFirst: true, or default to 0
  const getInitialQuestionIndex = () => {
    if (!questions || questions.length === 0) return 0;
    const showFirstIndex = questions.findIndex((q) => q.showFirst === true);
    return showFirstIndex !== -1 ? showFirstIndex : 0;
  };

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(
    getInitialQuestionIndex()
  );
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [submittedAnswers, setSubmittedAnswers] = useState({});
  const [showExplanation, setShowExplanation] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [imagePopup, setImagePopup] = useState({
    isOpen: false,
    src: "",
    alt: "",
  });

  useEffect(() => {
    if (isOpen && !startTime) {
      setStartTime(Date.now());
    }
  }, [isOpen, startTime]);

  // Reset to showFirst question (or index 0) when modal opens
  useEffect(() => {
    if (isOpen && questions && questions.length > 0) {
      const showFirstIndex = questions.findIndex((q) => q.showFirst === true);
      const initialIndex = showFirstIndex !== -1 ? showFirstIndex : 0;
      // Ensure the index is within bounds
      const validIndex = Math.max(
        0,
        Math.min(initialIndex, questions.length - 1)
      );
      setCurrentQuestionIndex(validIndex);
    } else if (isOpen && (!questions || questions.length === 0)) {
      setCurrentQuestionIndex(0);
    }
  }, [isOpen, questions]);

  useEffect(() => {
    let interval;
    if (isOpen && startTime) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isOpen, startTime]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Ensure currentQuestionIndex is within bounds and get current question safely
  const currentQuestion =
    questions &&
      questions.length > 0 &&
      currentQuestionIndex >= 0 &&
      currentQuestionIndex < questions.length
      ? questions[currentQuestionIndex]
      : null;
  const isAnswerSubmitted =
    currentQuestion && submittedAnswers[currentQuestionIndex] !== undefined;
  const selectedOption = currentQuestion
    ? selectedAnswers[currentQuestionIndex]
    : undefined;

  const handleOptionSelect = (optionIndex) => {
    if (!isAnswerSubmitted) {
      setSelectedAnswers((prev) => ({
        ...prev,
        [currentQuestionIndex]: optionIndex,
      }));
    }
  };

  const handleSubmitAnswer = () => {
    if (selectedOption !== undefined) {
      setSubmittedAnswers((prev) => ({
        ...prev,
        [currentQuestionIndex]: selectedOption,
      }));
      setShowExplanation(true);
    }
    // setCurrentQuestionIndex(0);
  };

  const handleNext = () => {
    if (questions && currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setShowExplanation(
        submittedAnswers[currentQuestionIndex + 1] !== undefined
      );
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
      setShowExplanation(
        submittedAnswers[currentQuestionIndex - 1] !== undefined
      );
    }
  };

  const handleQuestionJump = (questionIndex) => {
    if (questions && questionIndex >= 0 && questionIndex < questions.length) {
      setCurrentQuestionIndex(questionIndex);
      setShowExplanation(submittedAnswers[questionIndex] !== undefined);
    }
  };

  const handleImageClick = (src, alt) => {
    setImagePopup({ isOpen: true, src, alt });
  };

  const closeImagePopup = () => {
    setImagePopup({ isOpen: false, src: "", alt: "" });
  };

  const getOptionClass = (optionIndex) => {
    if (!currentQuestion) return "bg-white hover:bg-gray-50";

    if (!isAnswerSubmitted) {
      return selectedOption === optionIndex
        ? "bg-blue-100 border-blue-500"
        : "bg-white hover:bg-gray-50";
    }

    const correctIndex = parseInt(currentQuestion?.correctOptionIndex || 0);
    if (optionIndex === correctIndex) {
      return "bg-green-100 border-green-500";
    }
    if (optionIndex === selectedOption && optionIndex !== correctIndex) {
      return "bg-red-100 border-red-500";
    }
    return "bg-gray-50";
  };

  const getQuestionStatus = (questionIndex) => {
    if (submittedAnswers[questionIndex] !== undefined) {
      const isCorrect =
        submittedAnswers[questionIndex] ===
        parseInt(questions[questionIndex]?.correctOptionIndex || -1);
      return isCorrect ? "correct" : "incorrect";
    }
    if (selectedAnswers[questionIndex] !== undefined) {
      return "answered";
    }
    return "unanswered";
  };

  const getPaginationButtonClass = (questionIndex) => {
    const status = getQuestionStatus(questionIndex);
    const isActive = questionIndex === currentQuestionIndex;

    let baseClass =
      "w-8 h-8 rounded-full text-sm font-medium transition-all cursor-pointer flex items-center justify-center ";

    if (isActive) {
      baseClass += "ring-2 ring-blue-500 ring-offset-2 ";
    }

    switch (status) {
      case "correct":
        return baseClass + "bg-green-500 text-white hover:bg-green-600";
      case "incorrect":
        return baseClass + "bg-red-500 text-white hover:bg-red-600";
      case "answered":
        return baseClass + "bg-blue-500 text-white hover:bg-blue-600";
      default:
        return baseClass + "bg-gray-200 text-gray-700 hover:bg-gray-300";
    }
  };

  const resetQuiz = () => {
    if (questions && questions.length > 0) {
      const showFirstIndex = questions.findIndex((q) => q.showFirst === true);
      const initialIndex = showFirstIndex !== -1 ? showFirstIndex : 0;
      // Ensure the index is within bounds
      if (initialIndex >= 0 && initialIndex < questions.length) {
        setCurrentQuestionIndex(initialIndex);
      } else {
        setCurrentQuestionIndex(0);
      }
    } else {
      setCurrentQuestionIndex(0);
    }
    setSelectedAnswers({});
    setSubmittedAnswers({});
    setShowExplanation(false);
    setStartTime(null);
    setElapsedTime(0);
  };

  const handleClose = () => {
    resetQuiz();
    onClose();
  };

  if (!isOpen || !questions || questions.length === 0 || !currentQuestion)
    return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        title={`${subchapterTitle} - Question ${currentQuestion?.No || currentQuestionIndex + 1}`}
        footer={null}
        onCancel={handleClose}
        formWidth="max-w-[98%]"
        formHeight="max-h-[90vh]"
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center bg-blue-50 px-6 py-3 border-b">
            <div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <FontAwesomeIcon icon={faClock} />
                    <span className="font-mono">{formatTime(elapsedTime)}</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    Question {currentQuestion?.No || currentQuestionIndex + 1} of {questions.length}
                  </div>
                </div>
              </div>
              <div className="text-sm font-medium">
                <span className="text-green-600">
                  Correct:{" "}
                  {
                    Object.entries(submittedAnswers).filter(
                      ([qIndex, selectedIdx]) =>
                        selectedIdx ===
                        parseInt(
                          questions[parseInt(qIndex)]?.correctOptionIndex || -1
                        )
                    ).length
                  }
                </span>
                <span className="mx-2 text-gray-400">|</span>
                <span className="text-red-600">
                  Wrong:{" "}
                  {
                    Object.entries(submittedAnswers).filter(
                      ([qIndex, selectedIdx]) =>
                        selectedIdx !==
                        parseInt(
                          questions[parseInt(qIndex)]?.correctOptionIndex || -1
                        )
                    ).length
                  }
                </span>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <button
                className="text-red-600 hover:text-red-800 flex items-center gap-2 text-sm"
                onClick={() => {
                  /* Handle report functionality */
                }}
              >
                <FontAwesomeIcon icon={faFlag} />
                Report a Content Issue
              </button>
            </div>
          </div>
          {/* Submit Button and Navigation */}
          <div className="px-3 py-3 flex justify-between items-center my-2">
            {/* Pagination */}
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-4 flex-wrap">
                {questions.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuestionJump(index)}
                    className={getPaginationButtonClass(index)}
                    title={`Question ${item.No || index + 1} - ${getQuestionStatus(
                      index
                    )}`}
                  >
                    {item.No || index + 1}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-between items-center gap-3">
              <Button
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
                variant="outline"
                size="small"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />
                Previous
              </Button>

              <Button
                onClick={handleNext}
                disabled={currentQuestionIndex === questions.length - 1}
                variant="outline"
                size="small"
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} className="ml-2" />
              </Button>
            </div>
          </div>
          {/* Main Content */}
          <div className="flex-1 flex">
            {/* Question Section */}
            <div className="flex-1 p-3">
              <div className="max-w-5xl">
                {/* Question Text */}
                {currentQuestion?.questionText && (
                  <div
                    className="mb-3 text-gray-800"
                    dangerouslySetInnerHTML={{
                      __html: currentQuestion.questionText || "",
                    }}
                  />
                )}

                {/* Question Image */}
                {currentQuestion?.questionImage && (
                  <div className="mb-3">
                    <img
                      src={currentQuestion.questionImage}
                      alt="Question"
                      className="w-[850px] h-auto rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() =>
                        handleImageClick(
                          currentQuestion.questionImage,
                          "Question Image"
                        )
                      }
                    />
                  </div>
                )}
                {!isAnswerSubmitted && (
                  <Button
                    onClick={handleSubmitAnswer}
                    disabled={selectedOption === undefined}
                  >
                    Submit Answer
                  </Button>
                )}
                {/* Options */}
                <div className="mb-6 mt-3 ">
                  <div className="grid grid-cols-2 gap-4">
                    {currentQuestion?.options?.map((option, index) => (
                      <label
                        key={index}
                        className={`block p-4 border-2 rounded-lg cursor-pointer transition-all ${getOptionClass(
                          index + 1
                        )}`}
                        onClick={() => handleOptionSelect(index + 1)}
                      >
                        <div className="flex items-center">
                          <input
                            type="radio"
                            name={`question_${currentQuestionIndex}`}
                            checked={selectedOption === index + 1}
                            onChange={() => handleOptionSelect(index + 1)}
                            disabled={isAnswerSubmitted}
                            className="mr-3"
                          />
                          <span className="font-medium mr-2">
                            {String.fromCharCode(65 + index)}.
                          </span>
                          <div className="flex flex-col gap-2">
                            {index === 0 && currentQuestion?.optionAImage && (
                              <div>
                                <img
                                  src={currentQuestion?.optionAImage}
                                  alt="Option A"
                                  className="max-w-[250px] max-h-[200px] w-auto h-auto object-contain rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                />
                              </div>
                            )}
                            {index === 1 && currentQuestion?.optionBImage && (
                              <div>
                                <img
                                  src={currentQuestion?.optionBImage}
                                  alt="Option B"
                                  className="max-w-[250px] max-h-[200px] w-auto h-auto object-contain rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                />
                              </div>
                            )}
                            {index === 2 && currentQuestion?.optionCImage && (
                              <div>
                                <img
                                  src={currentQuestion?.optionCImage}
                                  alt="Option C"
                                  className="max-w-[250px] max-h-[200px] w-auto h-auto object-contain rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                />
                              </div>
                            )}
                            {index === 3 && currentQuestion?.optionDImage && (
                              <div>
                                <img
                                  src={currentQuestion?.optionDImage}
                                  alt="Option D"
                                  className="max-w-[250px] max-h-[200px] w-auto h-auto object-contain rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                />
                              </div>
                            )}
                            <span>{option}</span>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Solution Section */}
            <div className="w-[40%] bg-gray-50 border-l">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Detailed Guidance</h3>
                  <button
                    onClick={() => setShowExplanation(!showExplanation)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <FontAwesomeIcon
                      icon={showExplanation ? faEyeSlash : faEye}
                    />
                  </button>
                </div>

                {showExplanation && isAnswerSubmitted && (
                  <div>
                    <div className="mb-4">
                      <div className="bg-blue-100 p-3 rounded-lg mb-2">
                        <span className="font-semibold text-blue-800">
                          Solution
                        </span>
                      </div>

                      {/* Solution Text */}
                      {currentQuestion?.solutionText && (
                        <div
                          className="mb-4 text-gray-700"
                          dangerouslySetInnerHTML={{
                            __html: currentQuestion.solutionText || "",
                          }}
                        />
                      )}
                      {/* Solution Image */}
                      {currentQuestion?.solutionImage && (
                        <div className="flex flex-col gap-3 mb-4">
                          {currentQuestion.solutionImage.map((item, index) => (
                            <img
                              key={index}
                              src={item}
                              alt="Solution"
                              className="w-[850px] h-auto rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() =>
                                handleImageClick(item, "Solution Image")
                              }
                            />
                          ))}
                        </div>
                      )}

                      <div className="text-sm text-gray-600">
                        The answer is (
                        {String.fromCharCode(
                          65 +
                          parseInt(
                            (currentQuestion?.correctOptionIndex || 1) - 1
                          )
                        )}
                        ).
                      </div>
                    </div>
                  </div>
                )}

                {!showExplanation && (
                  <div className="text-center text-gray-500 py-8">
                    Click the eye icon to show explanation
                  </div>
                )}

                {!isAnswerSubmitted && (
                  <div className="text-center text-gray-500 py-8">
                    Submit your answer to see the explanation
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Image Popup Modal */}
      {imagePopup.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999]">
          <div className="relative max-w-[90vw] max-h-[90vh] p-4">
            <button
              onClick={closeImagePopup}
              className="absolute top-[-8px] right-[-8px] bg-white text-primary rounded-full w-8 h-8 flex items-center justify-center hover:bg-opacity-80 transition-all z-10"
            >
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </button>
            <img
              src={imagePopup.src}
              alt={imagePopup.alt}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            />
            <div className="text-white text-center mt-2 text-sm">
              {imagePopup.alt}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuizModal;
